import {
  type MaintenanceEntry,
  type MaintenanceModule,
  statusMeta,
  moduleMeta,
} from '@/mocks/saGlobalMaintenanceCenter';

interface MaintenanceDetailModalProps {
  entry: MaintenanceEntry | null;
  onClose: () => void;
  onEdit?: (entry: MaintenanceEntry) => void;
  onCancelMaintenance?: (entry: MaintenanceEntry) => void;
  onCompleteMaintenance?: (entry: MaintenanceEntry) => void;
}

export default function MaintenanceDetailModal({
  entry,
  onClose,
  onEdit,
  onCancelMaintenance,
  onCompleteMaintenance,
}: MaintenanceDetailModalProps) {
  if (!entry) return null;

  const status = statusMeta[entry.status];
  const isActive = entry.status === 'active';
  const isScheduled = entry.status === 'scheduled';

  function getModulesList(modules: MaintenanceModule[]) {
    if (!modules || modules.length === 0) return '—';
    const isAll = modules.includes('all');
    if (isAll) {
      return (
        <span className="inline-flex items-center gap-1.5">
          <i className="ri-global-line text-foreground-500 text-xs"></i>
          <span className="text-foreground-600 text-xs font-medium">Todos los módulos</span>
        </span>
      );
    }
    return (
      <div className="flex flex-wrap gap-1.5">
        {modules.map((md) => {
          const meta = moduleMeta[md];
          if (!meta) return null;
          return (
            <span key={md} className="inline-flex items-center gap-1 px-2 py-1 rounded-lg bg-background-200 text-foreground-600 text-[10px] font-medium">
              <i className={`${meta.icon} text-[10px]`}></i>
              {meta.label}
            </span>
          );
        })}
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-foreground-950/60 backdrop-blur-sm" onClick={onClose}></div>
      <div className="relative bg-background-50 rounded-2xl w-full max-w-2xl max-h-[90vh] border border-background-200 flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-background-200 shrink-0">
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${status.bg}`}>
              <i className={`${status.icon} ${status.color} text-lg`}></i>
            </div>
            <div>
              <div className="flex items-center gap-2">
                <span className="text-foreground-500 text-[10px] font-mono">{entry.id}</span>
                <span className={`flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-semibold ${status.bg} ${status.color}`}>
                  <span className={`w-1.5 h-1.5 rounded-full ${status.dot}`}></span>
                  {status.label}
                </span>
                <span className={`px-2 py-0.5 rounded-md text-[10px] font-semibold ${
                  entry.type === 'global'
                    ? 'bg-violet-50 text-violet-700'
                    : 'bg-sky-50 text-sky-700'
                }`}>
                  {entry.type === 'global' ? 'Global' : 'Por Módulo'}
                </span>
              </div>
              <h3 className="text-foreground-900 font-bold text-sm mt-1">{entry.name}</h3>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-xl bg-background-200 flex items-center justify-center text-foreground-500 hover:bg-foreground-950 hover:text-background-50 transition-colors cursor-pointer"
          >
            <i className="ri-close-line text-base"></i>
          </button>
        </div>

        {/* Body */}
        <div className="p-5 overflow-y-auto flex-1 space-y-4">
          {/* Description */}
          <div>
            <p className="text-foreground-600 text-xs leading-relaxed">{entry.description}</p>
          </div>

          {/* Timeline */}
          <div className="grid grid-cols-2 gap-3">
            <div className="bg-background-100 rounded-xl p-3">
              <p className="text-foreground-400 text-[10px] mb-1">Inicio</p>
              <p className="text-foreground-800 text-xs font-semibold">
                {new Date(entry.startDate).toLocaleDateString('es-ES', { day: 'numeric', month: 'short', year: 'numeric' })}
              </p>
              <p className="text-foreground-500 text-[10px]">
                {new Date(entry.startDate).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' })} UTC
              </p>
            </div>
            <div className="bg-background-100 rounded-xl p-3">
              <p className="text-foreground-400 text-[10px] mb-1">Finalización</p>
              <p className="text-foreground-800 text-xs font-semibold">
                {new Date(entry.endDate).toLocaleDateString('es-ES', { day: 'numeric', month: 'short', year: 'numeric' })}
              </p>
              <p className="text-foreground-500 text-[10px]">
                {new Date(entry.endDate).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' })} UTC · {entry.duration}
              </p>
            </div>
          </div>

          {/* Modules affected */}
          <div>
            <p className="text-foreground-400 text-[10px] font-semibold mb-2">MÓDULOS AFECTADOS</p>
            {getModulesList(entry.modules)}
          </div>

          {/* Custom message */}
          <div>
            <p className="text-foreground-400 text-[10px] font-semibold mb-2">MENSAJE PERSONALIZADO</p>
            <div className={`rounded-xl p-3 border ${
              entry.customPageTheme === 'dark'
                ? 'bg-foreground-950 text-background-50 border-foreground-800'
                : 'bg-background-100 text-foreground-700 border-background-200'
            }`}>
              <p className="text-xs leading-relaxed">{entry.customMessage}</p>
            </div>
          </div>

          {/* Access control */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div className="bg-background-100 rounded-xl p-3">
              <p className="text-foreground-400 text-[10px] font-semibold mb-2">IPs PERMITIDAS</p>
              {entry.allowedIPs.length > 0 ? (
                <div className="space-y-1">
                  {entry.allowedIPs.map((ip) => (
                    <div key={ip} className="flex items-center gap-1.5">
                      <i className="ri-computer-line text-foreground-400 text-[10px]"></i>
                      <code className="text-foreground-600 text-[10px] font-mono">{ip}</code>
                    </div>
                  ))}
                </div>
              ) : (
                <span className="text-foreground-400 text-[10px]">Ninguna configurada</span>
              )}
            </div>
            <div className="bg-background-100 rounded-xl p-3">
              <p className="text-foreground-400 text-[10px] font-semibold mb-2">USUARIOS PERMITIDOS</p>
              {entry.allowedUsers.length > 0 ? (
                <div className="space-y-1">
                  {entry.allowedUsers.map((user) => (
                    <div key={user} className="flex items-center gap-1.5">
                      <i className="ri-user-line text-foreground-400 text-[10px]"></i>
                      <span className="text-foreground-600 text-[10px]">{user}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <span className="text-foreground-400 text-[10px]">Ninguno configurado</span>
              )}
            </div>
            <div className="bg-background-100 rounded-xl p-3">
              <p className="text-foreground-400 text-[10px] font-semibold mb-2">ROLES PERMITIDOS</p>
              {entry.allowedRoles.length > 0 ? (
                <div className="space-y-1">
                  {entry.allowedRoles.map((role) => (
                    <div key={role} className="flex items-center gap-1.5">
                      <i className="ri-shield-user-line text-foreground-400 text-[10px]"></i>
                      <span className="text-foreground-600 text-[10px]">{role}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <span className="text-foreground-400 text-[10px]">Ninguno configurado</span>
              )}
            </div>
          </div>

          {/* Custom page */}
          <div className="flex items-center justify-between bg-background-100 rounded-xl p-3">
            <div className="flex items-center gap-2">
              <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                entry.customPageEnabled ? 'bg-emerald-100' : 'bg-foreground-200'
              }`}>
                <i className={`${entry.customPageEnabled ? 'ri-eye-line text-emerald-500' : 'ri-eye-off-line text-foreground-500'} text-sm`}></i>
              </div>
              <div>
                <p className="text-foreground-800 text-xs font-semibold">Página de mantenimiento</p>
                <p className="text-foreground-400 text-[10px]">
                  {entry.customPageEnabled
                    ? `Habilitada — Tema: ${entry.customPageTheme === 'dark' ? 'Oscuro' : 'Claro'}`
                    : 'Deshabilitada'}
                </p>
              </div>
            </div>
          </div>

          {/* Affected services */}
          <div>
            <p className="text-foreground-400 text-[10px] font-semibold mb-2">SERVICIOS AFECTADOS</p>
            <div className="flex flex-wrap gap-1.5">
              {entry.affectedServices.map((svc) => (
                <span key={svc} className="px-2.5 py-1 rounded-lg bg-amber-50 border border-amber-200 text-amber-700 text-[10px] font-medium">
                  {svc}
                </span>
              ))}
            </div>
          </div>

          {/* Metadata */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div>
              <p className="text-foreground-400 text-[10px] mb-0.5">Creado por</p>
              <p className="text-foreground-600 text-[10px]">{entry.createdBy}</p>
            </div>
            <div>
              <p className="text-foreground-400 text-[10px] mb-0.5">Fecha creación</p>
              <p className="text-foreground-600 text-[10px]">
                {new Date(entry.createdAt).toLocaleString('es-ES', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
              </p>
            </div>
            {entry.completedAt && (
              <div>
                <p className="text-foreground-400 text-[10px] mb-0.5">Completado</p>
                <p className="text-foreground-600 text-[10px]">
                  {new Date(entry.completedAt).toLocaleString('es-ES', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
                </p>
              </div>
            )}
            {entry.completedBy && (
              <div>
                <p className="text-foreground-400 text-[10px] mb-0.5">Completado por</p>
                <p className="text-foreground-600 text-[10px]">{entry.completedBy}</p>
              </div>
            )}
          </div>

          {/* Notes */}
          <div className="bg-background-100 rounded-xl p-3">
            <p className="text-foreground-400 text-[10px] font-semibold mb-1.5">NOTAS</p>
            <p className="text-foreground-600 text-xs leading-relaxed">{entry.notes}</p>
          </div>

          {/* Rollback plan */}
          <div className={`rounded-xl p-3 border ${
            entry.status === 'cancelled'
              ? 'bg-foreground-100 border-foreground-200'
              : 'bg-emerald-50 border-emerald-200'
          }`}>
            <p className="text-foreground-400 text-[10px] font-semibold mb-1.5">PLAN DE ROLLBACK</p>
            <p className="text-foreground-600 text-xs leading-relaxed">{entry.rollbackPlan}</p>
          </div>
        </div>

        {/* Actions footer */}
        {(isActive || isScheduled) && (
          <div className="flex items-center gap-3 p-5 border-t border-background-200 shrink-0">
            {onEdit && (
              <button
                onClick={() => onEdit(entry)}
                className="flex items-center gap-1.5 px-4 py-2.5 rounded-xl bg-background-100 text-foreground-600 text-xs font-semibold hover:bg-background-200 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-edit-line text-sm"></i>
                Editar
              </button>
            )}
            {isScheduled && onCancelMaintenance && (
              <button
                onClick={() => onCancelMaintenance(entry)}
                className="flex items-center gap-1.5 px-4 py-2.5 rounded-xl bg-red-50 text-red-600 text-xs font-semibold hover:bg-red-100 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-close-circle-line text-sm"></i>
                Cancelar
              </button>
            )}
            {isActive && onCompleteMaintenance && (
              <button
                onClick={() => onCompleteMaintenance(entry)}
                className="flex items-center gap-1.5 px-4 py-2.5 rounded-xl bg-emerald-50 text-emerald-600 text-xs font-semibold hover:bg-emerald-100 transition-colors cursor-pointer whitespace-nowrap ml-auto"
              >
                <i className="ri-check-line text-sm"></i>
                Finalizar mantenimiento
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}